/**
 * useRobiConversation — resolves (or creates) the AI agent conversation that
 * backs the Robi help chat, then fetches its full details.
 *
 * Resolution order:
 *   1. In-memory cache (followedAiAgent.conversationId)
 *   2. Key-value storage (STORAGE_KEYS.ROBI_CONVERSATION_ID)
 *   3. GET  Conversation/ListWithFilter  { filter: 'AgentDirect' }
 *   4. POST Conversation/Initiate
 *
 * Once a valid conversationId is obtained it is persisted to storage, written
 * back to the local profile, and used to GET Conversation/{conversationId}.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import conversationApi from '../api/conversation/conversationApi';
import messageService from '../api/services/messageService';
import { storage } from '../platform/storage';
import { STORAGE_KEYS } from '../constants';
import { useProfile } from '../context/ProfileContext';
import type { ConversationDto } from '../api/conversation/conversationTypes';
import type { FollowedAiAgent } from '../types/userProfile';

export interface UseRobiConversationResult {
  /** Resolved conversation ID, or null while loading / not yet resolved. */
  conversationId: number | null;
  /** Full conversation details from GET Conversation/{id}. */
  conversationDetails: ConversationDto | null;
  /** True while any API call is in flight. */
  loading: boolean;
  /** Error message if any step in the flow failed. */
  error: string | null;
  /** True when the profile has no AI agent to open a conversation with. */
  missingAiAgent: boolean;
  /** Manually re-trigger the full resolution flow (e.g. after an error). */
  retry: () => void;
}

/**
 * ListWithFilter answers with either a bare array or a paged envelope
 * depending on the endpoint version, so both shapes are unwrapped here.
 */
const extractConversationIdFromList = (data: unknown): number | null => {
  if (!data) return null;

  if (Array.isArray(data)) {
    const first = data[0] as ConversationDto | undefined;
    return first?.conversationId ?? null;
  }

  const envelope = data as Record<string, unknown>;
  const list =
    (envelope.conversations as ConversationDto[] | undefined) ??
    (envelope.items as ConversationDto[] | undefined) ??
    (envelope.data as ConversationDto[] | undefined);

  if (Array.isArray(list) && list.length > 0) {
    return (list[0] as ConversationDto).conversationId ?? null;
  }

  return null;
};

const useRobiConversation = (): UseRobiConversationResult => {
  const { profile, updateProfileLocal } = useProfile();
  const followedAiAgent: FollowedAiAgent | null | undefined =
    profile?.followedAiAgent;

  const [conversationId, setConversationId] = useState<number | null>(null);
  const [conversationDetails, setConversationDetails] =
    useState<ConversationDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Prevents duplicate runs when StrictMode double-invokes effects.
  const resolveInProgress = useRef(false);

  const persistConversationId = useCallback(
    async (id: number, agent: FollowedAiAgent) => {
      try {
        await storage.setItem(STORAGE_KEYS.ROBI_CONVERSATION_ID, id);
      } catch {
        // Non-fatal — the flow can still proceed without the cache.
      }
      try {
        await updateProfileLocal({
          followedAiAgent: { ...agent, conversationId: id },
        });
      } catch {
        // Non-fatal — an in-memory profile write failure doesn't block the UI.
      }
    },
    [updateProfileLocal],
  );

  useEffect(() => {
    if (!followedAiAgent?.agentId) return;
    if (resolveInProgress.current) return;
    resolveInProgress.current = true;

    let cancelled = false;

    const resolve = async () => {
      setLoading(true);
      setError(null);

      try {
        let resolvedId: number | null = followedAiAgent.conversationId ?? null;

        if (!resolvedId) {
          const stored = await storage.getItem<number>(
            STORAGE_KEYS.ROBI_CONVERSATION_ID,
          );
          if (typeof stored === 'number' && stored > 0) {
            resolvedId = stored;
          }
        }

        if (!resolvedId) {
          const listRes = await conversationApi.listWithFilter({
            pageNumber: 1,
            pageSize: 20,
            searchTerm: '',
            filter: 'AgentDirect',
          });
          const idFromList = extractConversationIdFromList(listRes.data);
          if (idFromList) {
            resolvedId = idFromList;
            await persistConversationId(resolvedId, followedAiAgent);
          }
        }

        if (!resolvedId) {
          const dto = await messageService.initiateAiAgentConversation({
            conversationType: 'AgentDirect',
            aiAgentUserId: followedAiAgent.agentId ?? null,
            conversationName: 'RobiChat',
            avatarUrl: followedAiAgent.profilePicture ?? null,
          });
          const idFromInitiate = dto?.conversationId ?? null;
          if (idFromInitiate) {
            resolvedId = idFromInitiate;
            await persistConversationId(resolvedId, followedAiAgent);
          }
        }

        if (cancelled) return;

        if (!resolvedId) {
          setError('Could not resolve a conversation for this AI agent.');
          setLoading(false);
          return;
        }

        setConversationId(resolvedId);

        const detailsRes = await conversationApi.getById(resolvedId);
        if (cancelled) return;

        setConversationDetails(detailsRes.data ?? null);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load Robi conversation.',
        );
      } finally {
        if (!cancelled) setLoading(false);
        resolveInProgress.current = false;
      }
    };

    resolve();

    return () => {
      cancelled = true;
      resolveInProgress.current = false;
    };
    // Re-runs when the user switches AI buddy, or on manual retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followedAiAgent?.agentId, retryCount]);

  const retry = useCallback(() => {
    setConversationId(null);
    setConversationDetails(null);
    setError(null);
    resolveInProgress.current = false;
    setRetryCount(count => count + 1);
  }, []);

  return {
    conversationId,
    conversationDetails,
    loading,
    error,
    missingAiAgent: !followedAiAgent?.agentId,
    retry,
  };
};

export default useRobiConversation;
